import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Form, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';

import { ImageLoader, Loading, Paper, RangeSliderInput } from 'componets/ui';
import { RangeSlider } from 'componets/common';
import { CheckBoxField, FormItem, SubmitButton } from 'componets/form';
import { SaleProjectNote } from './SaleProjectNote';
import { SaleProjectInterests } from './SaleProjectInterests';
import { RiskNotice } from 'pages/Account/Invest/RiskNotice';

import { useAppDispatch, useAppSelector } from 'hooks';

import { getSaleProject, saleInvestment } from 'features/myInvestments/actions';
import { resetMyInvestments } from 'features/myInvestments/myInvestmentsSlice';
import { getPrimaryMarketItem } from 'features/primaryMarket/actions';

import Utils from 'utils/utils';
import { IS_SOLD_INVESTMENT, Paths, Rules } from 'utils/constants';
import { getInStorage, removeFromStorage, language } from 'utils';

import { FieldData } from 'models';

import { ReactComponent as Success } from 'assets/icons/success-reset.svg';

import { selectIsAdmin } from 'selectors';

import s from './index.module.scss';

export function SaleProject() {
	const { saleProject, isSold, sellingPrice } = useAppSelector((state) => state.myInvestments);
	const isAdmin = useAppSelector(selectIsAdmin);
	const item = useAppSelector((state) => state.primaryMarket.item);

	const { t } = useTranslation();

	const location: any = useLocation();
	const { investmentId } = useParams();

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isOpen, setIsOpen] = useState(false);

	const projectId = location.state?.projectId;

	const getData = useCallback(async () => {
		if (investmentId) {
			const allPromise = Promise.all([
				dispatch(getSaleProject({ id: investmentId })),
				dispatch(getPrimaryMarketItem(projectId)),
			]);
			await allPromise;
		}
	}, [dispatch, investmentId, projectId]);

	useEffect(() => {
		getData().finally(() => {
			setIsLoading(false);
		});
	}, [dispatch, investmentId, projectId]);

	const amount = Form.useWatch('amount', form);

	const onFinishResult = () => {
		if (investmentId) {
			setIsSubmitting(true);
			dispatch(
				saleInvestment({
					id: investmentId,
					body: { amount: `${amount}`, commission: saleProject.commission },
				}),
			)
				.unwrap()
				.then(() => {})
				.catch((errors) => {
					setFields([
						{
							name: ['amount'],
							errors: [
								errors.message.includes('exists')
									? t('activeSaleExistText')
									: errors.message.includes('inactive')
									? t('errorInactiveSaleText')
									: t(errors.message),
							],
						},
					]);
				})

				.finally(() => {
					setIsOpen(false);
					setIsSubmitting(false);
				});
		}
	};

	const onFinish = () => {
		setIsOpen(true);
	};

	useEffect(() => {
		if (getInStorage(IS_SOLD_INVESTMENT) === 'true') {
			navigate(Paths.MY_INVESTMENTS);
		}
	}, [navigate]);

	useEffect(() => {
		return () => {
			dispatch(resetMyInvestments());
			removeFromStorage(IS_SOLD_INVESTMENT);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sellingFee = (+saleProject.commission * amount) / 100;
	const isLithuanian = language === 'lt';

	if (isLoading) return <Loading />;

	return (
		<div className={s.root}>
			<div className={s.buttons}>
				<Link className={s.link} to={Paths.MY_INVESTMENTS}>
					{t('fundedProjectsText')}
				</Link>
				<Link className={s.link} to={Paths.MY_INVESTMENTS}>
					{t('finishedInvestmentsText')}
				</Link>
			</div>
			{!!saleProject.minAmount ? (
				<Paper styleName={s.paper}>
					<div className={s.column}>
						{isSold ? (
							<div className={s.centerColumn}>
								<Success />

								<span>{t('investmentPlacedText')}</span>
							</div>
						) : (
							<>
								<Typography.Text className={s.title}>{item.title}</Typography.Text>

								<ImageLoader photo={item.photos && item.photos[0]} wrapperClassName={s.photo} />

								<SaleProjectInterests
									paid={saleProject.paidInterest}
									expected={+saleProject.expectedInterest}
								/>
								<SaleProjectNote validUntil={saleProject.validUntil} />
							</>
						)}
					</div>

					<div className={s.line} />

					<div className={s.column}>
						<Typography.Text className={s.title}>
							{isSold ? t('soldOverviewText') : t('sellMyInvestmentText')}
						</Typography.Text>
						{isSold ? (
							<>
								<div className={cn(s.row, s.rowSold)}>
									<div className={s.columnText}>
										<Typography.Text type="secondary" className={s.text}>
											{t('sellingPriceText')}
										</Typography.Text>
										<span className={s.text}>{Utils.moneyFormat(+sellingPrice)}</span>
									</div>

									<div className={s.columnText}>
										<Typography.Text type="secondary" className={s.text}>
											{t('initialPrincipalText')}
										</Typography.Text>
										<Typography.Text type="secondary" className={s.text}>
											{Utils.moneyFormat(+saleProject.remainingPrincipal)}
										</Typography.Text>
									</div>
								</div>

								<SaleProjectInterests
									paid={saleProject.paidInterest}
									expected={+saleProject.expectedInterest}
								/>

								<Link to={Paths.MY_INVESTMENTS} className={s.button}>
									{t('backFundedText')}
								</Link>

								<SaleProjectNote validUntil={saleProject.validUntil} />
							</>
						) : (
							<Form
								onFinish={onFinish}
								className={s.form}
								initialValues={{ amount: saleProject.remainingPrincipal }}
								onFieldsChange={setFields}
								fields={fields}
								form={form}
							>
								<Typography.Text type="secondary">{t('sellingPriceText')}</Typography.Text>

								<div className={s.rangeInput}>
									<RangeSliderInput
										maxPrice={saleProject.maxAmount}
										minPrice={saleProject.minAmount}
										setFields={setFields}
									/>
								</div>

								<div className={s.range}>
									<RangeSlider
										isMoney
										value={amount}
										defaultValue={+saleProject.remainingPrincipal}
										min={+saleProject.minAmount}
										max={+saleProject.maxAmount}
										onChange={(value: number) => {
											setFields([{ name: ['amount'], value }]);
										}}
									/>
								</div>

								<div className={s.rows}>
									<div className={s.row}>
										<Typography.Text type="secondary">
											{saleProject.commission}% {t('sellingFeeText')}:
										</Typography.Text>
										<span>{Utils.moneyFormat(sellingFee)}</span>
									</div>

									<div className={s.row}>
										<Typography.Text type="secondary">{t('youWillReceiveText')}:</Typography.Text>
										<span>{Utils.moneyFormat(+amount - sellingFee)}</span>
									</div>
								</div>

								<div className={s.columnForm}>
									<FormItem name="agree" rules={[Rules.REQUIRED_CHECKBOX]} className="checkbox">
										<CheckBoxField
											text={
												<span className={s.checkbox}>
													<Trans
														i18nKey="confirmAssigmentAgreementText"
														components={{
															agreement_link: (
																<a
																	href={
																		isLithuanian
																			? 'https://crowdpear.com/wp-content/uploads/2023/05/Reikalavimo_teises_perleidimo_sutarties_bendrosios_salygos.pdf'
																			: 'https://crowdpear.com/wp-content/uploads/2023/05/General_term_and_coditions_of_the_Assignment_agreement.pdf'
																	}
																	type="button"
																	target="_blank"
																	rel="noreferrer"
																/>
															),
														}}
													/>
												</span>
											}
										/>
									</FormItem>

									<div className="modal-footer">
										<SubmitButton
											text={t('sellText')}
											classNameWrapper={s.submitWrapper}
											className={cn(s.submit, { 'ant-btn-grey': isAdmin })}
										/>

										<Button onClick={() => navigate(Paths.MY_INVESTMENTS)} className={s.cancel}>
											{t('cancelText')}
										</Button>
									</div>
								</div>
							</Form>
						)}
					</div>
				</Paper>
			) : (
				<Loading />
			)}

			<RiskNotice
				onFinish={onFinishResult}
				loading={isSubmitting}
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
			/>
		</div>
	);
}
